// hooks/useDropdown.ts
import { useState, useRef, useEffect, useCallback } from "react";
import { DropdownOption, DropdownProps } from "./types";
// import { DropdownProps, isMultiSelect } from "./types";

// interface UseDropdownProps extends DropdownProps {}
interface UseDropdownProps {
  options: DropdownOption[];
  value: string | string[];
  onChange: (value: string | string[]) => void;
  searchable: boolean;
  multiSelect: boolean;
}
interface UseDropdownBaseConfig {
  options: DropdownOption[];
  searchable: boolean;
  disabled: boolean;
}

interface UseDropdownSingleConfig extends UseDropdownBaseConfig {
  multiSelect: false;
  value: string;
  onChange: (value: string) => void;
}

interface UseDropdownMultiConfig extends UseDropdownBaseConfig {
  multiSelect: true;
  value: string[];
  onChange: (value: string[]) => void;
}

type UseDropdownConfig = UseDropdownSingleConfig | UseDropdownMultiConfig;

export const useDropdown = (props: DropdownProps) => {
  const {
    options,
    value,
    onChange,
    searchable = false,
    disabled = false,
  } = props;

  const multiSelect = props.multiSelect || false;

  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const filteredOptions = options.filter((option) =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const isSelected = useCallback(
    (optionValue: string) => {
      if (multiSelect) {
        return Array.isArray(value) && value.includes(optionValue);
      }
      return value === optionValue;
    },
    [value, multiSelect]
  );

  const handleOptionSelect = useCallback(
    (optionValue: string) => {
      if (multiSelect) {
        const currentValue = Array.isArray(value) ? value : [];
        const newValue = currentValue.includes(optionValue)
          ? currentValue.filter((v) => v !== optionValue)
          : [...currentValue, optionValue];

        // We know this is safe because multiSelect is true
        (onChange as (value: string[]) => void)(newValue);
      } else {
        // We know this is safe because multiSelect is false
        (onChange as (value: string) => void)(optionValue);
        setIsOpen(false);
        setSearchTerm("");
      }
    },
    [value, multiSelect, onChange]
  );

  const getSelectedLabels = useCallback(() => {
    if (multiSelect && Array.isArray(value)) {
      const selectedOptions = options
        .filter((option) => value.includes(option.value))
        .map((option) => option.label);

      return selectedOptions.length > 0 ? selectedOptions.join(", ") : "";
    }

    if (!multiSelect && typeof value === "string" && value) {
      return options.find((option) => option.value === value)?.label || "";
    }

    return "";
  }, [value, multiSelect, options]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!isOpen) return;

      switch (event.key) {
        case "ArrowDown":
          event.preventDefault();
          setHighlightedIndex((prev) =>
            prev < filteredOptions.length - 1 ? prev + 1 : prev
          );
          break;
        case "ArrowUp":
          event.preventDefault();
          setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : prev));
          break;
        case "Enter":
          event.preventDefault();
          if (
            filteredOptions[highlightedIndex] &&
            !filteredOptions[highlightedIndex].disabled
          ) {
            handleOptionSelect(filteredOptions[highlightedIndex].value);
          }
          break;
        case "Escape":
          setIsOpen(false);
          setSearchTerm("");
          break;
      }
    },
    [isOpen, filteredOptions, highlightedIndex, handleOptionSelect]
  );

  useEffect(() => {
    if (isOpen && searchable && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen, searchable]);

  useEffect(() => {
    setHighlightedIndex(0);
  }, [searchTerm, filteredOptions]);

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return {
    isOpen,
    setIsOpen,
    searchTerm,
    setSearchTerm,
    highlightedIndex,
    setHighlightedIndex,
    filteredOptions,
    isSelected,
    handleOptionSelect,
    getSelectedLabels,
    dropdownRef,
    searchInputRef,
    multiSelect,
  };
};
// import { useState, useRef, useEffect, useCallback } from "react";
// import { DropdownOption } from "./types";

// interface UseDropdownProps {
//   options: DropdownOption[];
//   value: string | string[];
//   onChange: (value: string | string[]) => void;
//   searchable: boolean;
//   multiSelect: boolean;
// }

// export const useDropdown = ({
//   options,
//   value,
//   onChange,
//   searchable,
//   multiSelect,
// }: UseDropdownProps) => {
//   const [isOpen, setIsOpen] = useState(false);
//   const [searchTerm, setSearchTerm] = useState("");
//   const [highlightedIndex, setHighlightedIndex] = useState(0);
//   const dropdownRef = useRef<HTMLDivElement>(null);
//   const searchInputRef = useRef<HTMLInputElement>(null);

//   const filteredOptions = options.filter((option) =>
//     option.label.toLowerCase().includes(searchTerm.toLowerCase())
//   );

//   const isSelected = useCallback(
//     (optionValue: string) => {
//       if (multiSelect) {
//         return Array.isArray(value) && value.includes(optionValue);
//       }
//       return value === optionValue;
//     },
//     [value, multiSelect]
//   );

//   const handleOptionSelect = useCallback(
//     (optionValue: string) => {
//       if (multiSelect) {
//         const currentValue = Array.isArray(value) ? value : [];
//         const newValue = currentValue.includes(optionValue)
//           ? currentValue.filter((v) => v !== optionValue)
//           : [...currentValue, optionValue];
//         onChange(newValue);
//       } else {
//         onChange(optionValue);
//         setIsOpen(false);
//         setSearchTerm("");
//       }
//     },
//     [value, multiSelect, onChange]
//   );

//   const getSelectedLabels = useCallback(() => {
//     if (multiSelect && Array.isArray(value)) {
//       return options
//         .filter((option) => value.includes(option.value))
//         .map((option) => option.label)
//         .join(", ");
//     }

//     if (!multiSelect && typeof value === "string") {
//       return options.find((option) => option.value === value)?.label || "";
//     }

//     return "";
//   }, [value, multiSelect, options]);

//   const handleKeyDown = useCallback(
//     (event: KeyboardEvent) => {
//       if (!isOpen) return;

//       switch (event.key) {
//         case "ArrowDown":
//           event.preventDefault();
//           setHighlightedIndex((prev) =>
//             prev < filteredOptions.length - 1 ? prev + 1 : prev
//           );
//           break;
//         case "ArrowUp":
//           event.preventDefault();
//           setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : prev));
//           break;
//         case "Enter":
//           event.preventDefault();
//           if (filteredOptions[highlightedIndex]) {
//             handleOptionSelect(filteredOptions[highlightedIndex].value);
//           }
//           break;
//         case "Escape":
//           setIsOpen(false);
//           setSearchTerm("");
//           break;
//       }
//     },
//     [isOpen, filteredOptions, highlightedIndex, handleOptionSelect]
//   );

//   useEffect(() => {
//     if (isOpen && searchable && searchInputRef.current) {
//       searchInputRef.current.focus();
//     }
//   }, [isOpen, searchable]);

//   useEffect(() => {
//     setHighlightedIndex(0);
//   }, [searchTerm]);

//   useEffect(() => {
//     document.addEventListener("keydown", handleKeyDown);
//     return () => document.removeEventListener("keydown", handleKeyDown);
//   }, [handleKeyDown]);

//   useEffect(() => {
//     const handleClickOutside = (event: MouseEvent) => {
//       if (
//         dropdownRef.current &&
//         !dropdownRef.current.contains(event.target as Node)
//       ) {
//         setIsOpen(false);
//         setSearchTerm("");
//       }
//     };

//     document.addEventListener("mousedown", handleClickOutside);
//     return () => document.removeEventListener("mousedown", handleClickOutside);
//   }, []);

//   return {
//     isOpen,
//     setIsOpen,
//     searchTerm,
//     setSearchTerm,
//     highlightedIndex,
//     setHighlightedIndex,
//     filteredOptions,
//     isSelected,
//     handleOptionSelect,
//     getSelectedLabels,
//     dropdownRef,
//     searchInputRef,
//   };
// };
